/**
 * Third-party dashboards we embed.
 *
 * These URLs carry an **access token**, so they come from the environment (D-47) and never
 * from a constant in this repository — a committed token is a leaked token. The long-term
 * fix is a token minted per session by our backend (Luzmo signs embed tokens with an API
 * key, so the browser never sees a long-lived one); until then a time-limited token in
 * `.env` proves the panel.
 *
 * **One shop, one dashboard.** A multi-store account gets a shop selector, so each shop
 * needs its own Luzmo link: `VITE_MOBIETRAIN_INSIGHTS_URLS` is a JSON object keyed by the
 * three-digit store code (`{"022": "https://…", "001": "https://…"}`). A single-store
 * account keeps the plain `VITE_MOBIETRAIN_INSIGHTS_URL` and sees no selector.
 */
const env = import.meta.env as unknown as Record<string, string | undefined>

/** The single-store link (also the fallback when a shop has no dedicated link). */
export const MOBIETRAIN_INSIGHTS_ENV = 'VITE_MOBIETRAIN_INSIGHTS_URL'

/** JSON map of three-digit store code → that shop's own dashboard URL. */
export const MOBIETRAIN_INSIGHTS_URLS_ENV = 'VITE_MOBIETRAIN_INSIGHTS_URLS'

/** Where a resolved link came from — the page warns when a shop falls back. */
export type InsightLinkSource = 'shop' | 'default' | 'none'

export interface InsightLink {
  url: string
  source: InsightLinkSource
}

/**
 * Canonical three-digit store code: `22`, `"22"` and `"022"` all become `"022"`.
 *
 * Store numbers are the canonical key and the code is their zero-padded display form
 * (`app/models/store.py`), so this is a formatting rule rather than a lookup. Anything
 * that is not 1-3 digits has no code and yields an empty string.
 */
export function normalizeStoreCode(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  const text = String(value).trim().replace(/^#/, '')
  if (!/^\d{1,3}$/.test(text)) return ''
  return text.padStart(3, '0')
}

/**
 * Parse the `VITE_MOBIETRAIN_INSIGHTS_URLS` JSON map.
 *
 * Deliberately forgiving: a malformed value or a bad entry must never crash the page — it
 * is dropped instead, and the page then says which shop is missing a link. Only absolute
 * http(s) URLs are accepted, so a stray `javascript:` value cannot become an iframe source.
 */
export function parseShopInsightUrls(raw: string | undefined): Record<string, string> {
  const text = (raw ?? '').trim()
  if (!text) return {}

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return {}
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}

  const urls: Record<string, string> = {}
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value !== 'string') continue
    const code = normalizeStoreCode(key)
    const url = value.trim()
    if (!code || !/^https?:\/\//i.test(url)) continue
    urls[code] = url
  }
  return urls
}

export const MOBIETRAIN_INSIGHTS_URL = (env[MOBIETRAIN_INSIGHTS_ENV] ?? '').trim()

export const MOBIETRAIN_INSIGHTS_SHOP_URLS = parseShopInsightUrls(
  env[MOBIETRAIN_INSIGHTS_URLS_ENV],
)

/** True when at least one shop has its own dedicated link. */
export const MOBIETRAIN_INSIGHTS_PER_SHOP = Object.keys(MOBIETRAIN_INSIGHTS_SHOP_URLS).length > 0

/**
 * The dashboard link for one shop.
 *
 * A dedicated per-shop link always wins. When the shop has none, the single link is used
 * as the fallback (so an existing single-link configuration keeps working); the page says
 * so explicitly, because that fallback may be another shop's numbers. With neither
 * configured the result is empty and the panel explains exactly what to set.
 */
export function resolveInsightLink(
  storeCode: string | number | null | undefined,
  shopUrls: Record<string, string> = MOBIETRAIN_INSIGHTS_SHOP_URLS,
  fallbackUrl: string = MOBIETRAIN_INSIGHTS_URL,
): InsightLink {
  const code = normalizeStoreCode(storeCode)
  const shopUrl = code ? (shopUrls[code] ?? '') : ''
  if (shopUrl) return { url: shopUrl, source: 'shop' }
  if (fallbackUrl) return { url: fallbackUrl, source: 'default' }
  return { url: '', source: 'none' }
}

/** `resolveInsightLink` against the configured environment. */
export function mobietrainInsightLink(storeCode?: string | number | null): InsightLink {
  return resolveInsightLink(storeCode)
}

